import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ICrossSell, ICrossSellDecline } from 'src/app/domain/interfaces/crossell/interface-cross-sell';
import { CrossSellsGateway } from 'src/app/domain/models/crossell/gateway/cross-sells-gateway';
import { AlertService } from '../alert-service/alert.service';
import { HttpClient } from '@angular/common/http';
import { LoadingType } from 'src/app/domain/models/alert/loading-type-enum';
import { first, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CrossSellPage } from 'src/app/domain/models/crossell/cross-sell-page-enum';
import { CrossSellCompany } from 'src/app/domain/models/crossell/cross-sell-company-enum';
import { IBoarwalkCrosssellData, ICrossSellComponentData } from 'src/app/domain/interfaces/crossell/interface-cross-sell-component';
import { Crosssell5Component } from 'src/app/UI/view-models/register-funnels/cross-sell/crosssell5/crosssell5.component';
import { Crosssell7Component } from 'src/app/UI/view-models/register-funnels/cross-sell/crosssell7/crosssell7.component';

@Injectable({
  providedIn: 'root'
})
export class CrossSellsService extends CrossSellsGateway {
  private crossSellsData: BehaviorSubject<Array<ICrossSell>> = new BehaviorSubject<Array<ICrossSell>>(undefined!);
  private crossSellComponentsData: Array<ICrossSellComponentData> = [
    {
      company: CrossSellCompany.PersonalLoan,
      component: Crosssell5Component,
    },
    {
      company: CrossSellCompany.CreditCards,
      component: Crosssell7Component,
      trackingLink: "https://www.bhmediatrack.com/cmp/HG54RQ/2BS8F3M/?sub1=&sub2=&sub3="
    },
    {
      company: CrossSellCompany.PersonalLoans,
      component: Crosssell7Component,
      trackingLink: "https://www.bhmediatrack.com/cmp/HG54RQ/2BTHB44/?sub1=&sub2=&sub3="
    },
    {
      company: CrossSellCompany.Payday,
      component: Crosssell7Component,
      trackingLink: "https://www.bhmediatrack.com/XCJPFQ/658NTX6/?sub1=&sub2=&sub3="
    },
    {
      company: CrossSellCompany.SurveyCashDeals,
      component: Crosssell7Component,
      trackingLink: "https://www.bhmediatrack.com/cmp/HG54RQ/2C3PR6F/?sub1=&sub2=&sub3="
    },
  ];

  constructor(private http: HttpClient, private alertService: AlertService) {
    super();
  }

  public getCrossSellsData(): Observable<Array<ICrossSell>> {
    return this.crossSellsData?.asObservable().pipe();
  }

  public initCrossSellsData() {
    this.alertService.setLoading(true, LoadingType.Default, ['Loading...']);
    this.requestCrossSellsData().pipe(first()).subscribe({
      next: (crossSellsDataResponse: Array<ICrossSell>) => {
        this.alertService.setLoading(false);
      },
      error: (error: any) => {
        this.alertService.setLoading(false);
      },
    });
  }

  private requestCrossSellsData(): Observable<Array<ICrossSell>> {
    return this.getCrossSellsDataFromAdminAPI().pipe(map((crossSellsDataResponse: Array<ICrossSell>) => {
      this.crossSellsData?.next(crossSellsDataResponse);
      return crossSellsDataResponse;
    }));
  }

  private getCrossSellsDataFromAdminAPI(): Observable<Array<ICrossSell>> {
    return this.http.get<{ data: Array<ICrossSell> }>(`${environment.apiNodeUrl}/cross-sells`).pipe(map((response: { data: Array<ICrossSell> }) => {
      return response?.data;
    }));
  }

  public getCrossSellsDataForPage(crossSellsData: Array<ICrossSell>, crossSellPage: CrossSellPage, offerId: number, affiliateId: number): Array<ICrossSellComponentData> {
    if (!crossSellsData?.length) return [];
    crossSellsData.forEach(crossSell => {
      const page = crossSell.pages?.find(page => page?.page?.toString() == CrossSellPage[crossSellPage]);
      if (crossSell.isActive) {
        const crossSellComponent = this.crossSellComponentsData?.find(crossSellComponent => CrossSellCompany[crossSellComponent.company] == crossSell.company?.toString())!;
        if (crossSellComponent) {
          crossSellComponent.name = crossSell?.name;
          crossSellComponent.isActive = crossSell?.isActive;
          crossSellComponent.order = page?.order!;
          crossSellComponent.trackingLink = crossSell.pages?.find(page => page?.page?.toString() == CrossSellPage[crossSellPage])?.trackingLink! ?? crossSellComponent.trackingLink;
          crossSellComponent!.show = true;
          if ((offerId && !page?.enableForAllOffers && !page?.offers?.some(offer => offer == offerId)) || (!offerId && !page?.enableForAllOffers)) {
            crossSellComponent!.show = false;
          }

          if ((affiliateId && !page?.enableForAllAffiliates && !page?.affiliates?.some(affiliate => affiliate == affiliateId)) || (!affiliateId && !page?.enableForAllAffiliates)) {
            crossSellComponent!.show = false;
          }
        }
      }
    });
    this.crossSellComponentsData.sort((a, b) => a.order! - b.order!);
    return this.crossSellComponentsData;
  }

  public getCrossSellsDeclineDataFromJSON(): Observable<ICrossSellDecline[]> {
    const pathJSON = 'assets/data/custom-data-for-crosell-decline.json';
    return this.http.get<{ CustomDataForCrossellsDecline: any[] }>(pathJSON).pipe(
      map(response => response.CustomDataForCrossellsDecline
        .map(item => item.DeclinePage)
        .reduce((acc, val) => acc.concat(val), [])),
      map((pages: any[]) => pages.map((page: any) => ({
        icon: page.Icon,
        vertical: page.Vertical,
        headline: page.Headline,
        body: page.Body,
        cta: page.CTA,
        trackingLink: page.TrackingLink,
        company: page.Company
      } as ICrossSellDecline)))
    );
  }


  public getBoardwalkCrossSells(path: string): Observable<IBoarwalkCrosssellData> {
    return this.http.get<{ data: IBoarwalkCrosssellData[] }>(`${environment.apiNodeUrl}/cross-sells-boardwalk`).pipe(map((response: { data: IBoarwalkCrosssellData[] }) => {
      return response.data.find((x: IBoarwalkCrosssellData) => x.boardwalkPath == path)!;
    }));
  }
}